import { useEffect, useRef, useState } from 'react';
import apiClient from '../../../Services/ApiClient';
import { useEffectAsync } from '../../../helpers';
import useNotification from '../../../Hooks/useNotification.hook';
import { useAclContext } from '../../Acl/Components/AclContextProvider';
import useScope from '../../Scope/Hooks/useScope';
import { usePatientTreatmentContext } from '../../Patient/Components/PatientTreatment/ContextProvider';
import {
	useLazyTermItemsQuery,
	useLazyUserCompareDataQuery,
	useLazyUserNcziProfileQuery,
} from '../../../graphql/graphql';
import {
	mapApiPatientDataToCompareTable,
	mapNCZIDataToCompareTable,
	PatientDataCompareKey,
} from '../../../Helpers/Patient/mapPatientDataToCompareTable.helper';
import { useNCZIContext } from '../Services/NCZIContext';
import { useNCZICommandContext } from '../Services/NCZICommandContext';
import { consumeCaseForPatientAutoVerify } from '../Services/patientAutoVerify.service';
import {
	findPatientDataMismatchKeys,
	resolveNcziInsuranceKey,
} from '../Services/patientDataMismatch.service';
import useNCZI from './useNCZI';
import useNCZICommandProcess from './useNCZICommandProcess';
import { VerifyPatientInsurance } from '../Commands/VerifyPatientInsurance';

export type PatientAutoVerifyStep =
	| 'off'
	| 'identifier'
	| 'informations'
	| 'insurance'
	| 'finished';

export type PatientAutoVerifyDialog =
	| 'mismatch'
	| 'error'
	| 'nonPayer'
	| 'notInsured';

const IDENTIFIER_OPERATION = 'DajJRUZIdentifikator_GW_v2';
const INFORMATIONS_OPERATION = 'DajPacientskySumarKontaktneUdaje_v4';
const INSURANCE_OPERATION = 'OverPoistenca_v6';

const NON_TERMINAL_STATES = ['done', 'error', 'offline', 'manual'];

interface InsuranceCommandResult {
	isInsured: boolean;
	isInsuredInEU: boolean;
	nonPayer: boolean;
	deferredZsEligibility: boolean | undefined;
	insurance: 'union' | 'dovera' | 'vszp';
}

export default function useNcziPatientAutoVerify(justAssigned = false) {
	const { patientId, caseId, isMyCase } = usePatientTreatmentContext();
	const { isSlovakia } = useScope();
	const { isAllowed } = useAclContext();
	const {
		showNotificationSuccess,
		showNotificationWarning,
		showNotificationLoading,
		dismissNotification,
	} = useNotification();

	const {
		patientBirthNumber,
		setPatientBirthNumber,
		patientMeddiInsurance,
		setPatientMeddiInsurance,
		patientIdentifier,
		patientSummaryContactData,
		setPatientInsurance,
		setPatientInsuranceNonPayer,
		setDeferredZsEligibility,
	} = useNCZIContext();
	const { commands } = useNCZICommandContext();
	const { fetchPatientIdentifier, fetchPatientSummaryContactData } = useNCZI();

	const [getUserNcziProfile] = useLazyUserNcziProfileQuery();
	const [getUserCompareData] = useLazyUserCompareDataQuery();
	const [getTermItems] = useLazyTermItemsQuery();

	const [step, setStep] = useState<PatientAutoVerifyStep>('off');
	const [dialogs, setDialogs] = useState<PatientAutoVerifyDialog[]>([]);
	const [mismatchKeys, setMismatchKeys] = useState<PatientDataCompareKey[]>([]);
	const [insuranceKey, setInsuranceKey] = useState<string | undefined>(
		undefined,
	);
	const [watchedCommandId, setWatchedCommandId] = useState<string | undefined>(
		undefined,
	);
	const [failedStep, setFailedStep] = useState<PatientAutoVerifyStep | null>(
		null,
	);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(
		undefined,
	);
	const startedRef = useRef<boolean>(false);
	const informationsVerifiedRef = useRef<boolean>(false);
	const mismatchFoundRef = useRef<boolean>(false);
	const handledCommandsRef = useRef<string[]>([]);
	const insuranceRequestedRef = useRef<boolean>(false);
	const loadingToastRef = useRef<number | string | undefined>(undefined);

	const clearLoadingToast = () => {
		if (typeof loadingToastRef.current !== 'undefined') {
			dismissNotification(loadingToastRef.current);
			loadingToastRef.current = undefined;
		}
	};

	const startLoadingToast = (tx: string) => {
		clearLoadingToast();
		loadingToastRef.current = showNotificationLoading({ content: tx });
	};

	const resolveLoadingToast = (tx: string) => {
		showNotificationSuccess({ content: tx, id: loadingToastRef.current });
		loadingToastRef.current = undefined;
	};

	const pushDialog = (dialog: PatientAutoVerifyDialog) => {
		clearLoadingToast();
		setDialogs(prevState =>
			dialog === 'error' && prevState.indexOf('error') !== -1
				? prevState
				: [...prevState, dialog],
		);
	};

	const skipWithWarning = (tx: string) => {
		clearLoadingToast();
		setStep('finished');
		setWatchedCommandId(undefined);
		showNotificationWarning({ content: tx });
	};

	const continueAfterFailure = (failedAtStep: PatientAutoVerifyStep) => {
		if (failedAtStep !== 'identifier' && failedAtStep !== 'informations') {
			setStep('finished');
			return;
		}

		if (!patientMeddiInsurance) {
			setStep('finished');
			return;
		}

		setInsuranceKey(patientMeddiInsurance);
		setStep('insurance');
	};

	const failWithModal = (
		stepToRetry: PatientAutoVerifyStep,
		message?: string,
	) => {
		setWatchedCommandId(undefined);
		setFailedStep(stepToRetry);
		setErrorMessage(message);
		pushDialog('error');
		continueAfterFailure(stepToRetry);
	};

	const findRunningCommandId = (operation: string) =>
		Object.values(commands).find(
			item =>
				item.operation === operation &&
				NON_TERMINAL_STATES.indexOf(item.state) === -1,
		)?.id;

	const isEnabled =
		!!caseId &&
		!!patientId &&
		isMyCase &&
		isSlovakia &&
		isAllowed('patient_health_ezdravie');

	useEffectAsync(async () => {
		if (!isEnabled || startedRef.current) {
			return;
		}

		if (!consumeCaseForPatientAutoVerify(caseId) && !justAssigned) {
			return;
		}

		startedRef.current = true;

		try {
			const { user } = await getUserNcziProfile({ id: patientId }).unwrap();
			const birthNumber =
				user?.profile?.birthNumber ?? user?.profile?.insuranceNumber;
			if (!birthNumber) {
				setStep('finished');
				return;
			}

			setPatientBirthNumber(birthNumber);
			setPatientMeddiInsurance(
				user?.profile?.insuranceCompanyTermItem?.value3 ?? null,
			);

			const availabilityResponse = await apiClient.get('/nczi');
			const { available } = availabilityResponse.data ?? {};

			if (!available) {
				skipWithWarning('nczi.patientAutoVerify.skippedCardConnectOffline');
				return;
			}

			startLoadingToast('nczi.patientAutoVerify.checkingInformations');
			setStep('identifier');
		} catch {
			setStep('finished');
		}
	}, [isEnabled, caseId, patientId]);

	useEffectAsync(async () => {
		if (step !== 'identifier' || !patientBirthNumber) {
			return;
		}

		if (patientIdentifier) {
			setStep('informations');
			return;
		}

		const runningIdentifierId = findRunningCommandId(IDENTIFIER_OPERATION);
		if (runningIdentifierId) {
			setWatchedCommandId(runningIdentifierId);
			return;
		}

		let command;
		try {
			command = await fetchPatientIdentifier(patientBirthNumber);
		} catch {
			continueAfterFailure('identifier');
			return;
		}

		if ('error' in command && command.error) {
			continueAfterFailure('identifier');
			return;
		}

		if (command.state === 'offline' || command.state === 'manual') {
			skipWithWarning('nczi.patientAutoVerify.skippedQueued');
			return;
		}

		setWatchedCommandId(command.id);
	}, [step, patientBirthNumber]);

	useEffect(() => {
		if (step !== 'identifier' || !patientIdentifier) {
			return;
		}

		setWatchedCommandId(undefined);
		setStep('informations');
	}, [step, patientIdentifier]);

	useEffectAsync(async () => {
		if (step !== 'informations' || !patientIdentifier) {
			return;
		}

		if (patientSummaryContactData) {
			return;
		}

		const runningInformationsId = findRunningCommandId(INFORMATIONS_OPERATION);
		if (runningInformationsId) {
			setWatchedCommandId(runningInformationsId);
			return;
		}

		let command;
		try {
			command = await fetchPatientSummaryContactData(
				patientIdentifier,
				patientId,
			);
		} catch {
			continueAfterFailure('informations');
			return;
		}

		if ('error' in command && command.error) {
			continueAfterFailure('informations');
			return;
		}

		if (command.state === 'offline' || command.state === 'manual') {
			skipWithWarning('nczi.patientAutoVerify.skippedQueued');
			return;
		}

		setWatchedCommandId(command.id);
	}, [step, patientIdentifier]);

	useEffectAsync(async () => {
		if (
			step !== 'informations' ||
			!patientSummaryContactData ||
			informationsVerifiedRef.current
		) {
			return;
		}

		informationsVerifiedRef.current = true;
		setWatchedCommandId(undefined);

		try {
			const compareData = await getUserCompareData({ id: patientId }).unwrap();

			if (compareData?.user && patientBirthNumber) {
				const { virtual } = mapApiPatientDataToCompareTable(compareData);
				const ncziData = mapNCZIDataToCompareTable(
					patientBirthNumber,
					patientSummaryContactData,
				);
				const foundMismatchKeys = findPatientDataMismatchKeys(
					ncziData,
					virtual,
				);
				if (foundMismatchKeys.length) {
					mismatchFoundRef.current = true;
					setMismatchKeys(foundMismatchKeys);
					pushDialog('mismatch');
				} else {
					resolveLoadingToast('nczi.patientAutoVerify.informationsVerified');
				}
			}

			const { termItems } = await getTermItems({
				termType: 'insurance_companies',
			}).unwrap();
			const resolvedInsuranceKey =
				resolveNcziInsuranceKey(
					patientSummaryContactData.patientJRUZ,
					termItems?.data ?? [],
				) ?? patientMeddiInsurance;

			if (!resolvedInsuranceKey) {
				skipWithWarning('nczi.patientAutoVerify.skippedUnknownInsurance');
				return;
			}

			setInsuranceKey(resolvedInsuranceKey);
			setStep('insurance');
		} catch {
			setStep('finished');
		}
	}, [step, patientSummaryContactData]);

	const handleInsuranceResult = (result: InsuranceCommandResult) => {
		setWatchedCommandId(undefined);
		setStep('finished');

		const isInsured = result?.isInsured || result?.isInsuredInEU;

		if (!isInsured) {
			pushDialog('notInsured');
			return;
		}

		setPatientInsurance(result.insurance ?? insuranceKey);
		setPatientInsuranceNonPayer(result.nonPayer);
		if (typeof result.deferredZsEligibility !== 'undefined') {
			setDeferredZsEligibility(result.deferredZsEligibility);
		}

		if (result.nonPayer) {
			pushDialog('nonPayer');
			return;
		}

		resolveLoadingToast('nczi.verifyPatientInsurance.foundActive');
	};

	const { processCommand } = useNCZICommandProcess({
		hideSuccess: true,
		hideError: true,
		onFinish: (id, result: InsuranceCommandResult) =>
			handleInsuranceResult(result),
		onError: (id, message) => failWithModal('insurance', message),
	});

	useEffectAsync(async () => {
		if (
			step !== 'insurance' ||
			!insuranceKey ||
			!patientBirthNumber ||
			insuranceRequestedRef.current ||
			dialogs.length
		) {
			return;
		}

		insuranceRequestedRef.current = true;
		startLoadingToast('nczi.patientAutoVerify.checkingInsurance');

		const runningInsuranceId = findRunningCommandId(INSURANCE_OPERATION);
		if (runningInsuranceId) {
			setWatchedCommandId(runningInsuranceId);
			return;
		}

		try {
			const command = await processCommand(
				VerifyPatientInsurance({
					insurance: insuranceKey,
					birthNumber: patientBirthNumber,
					patientId,
				}),
			);

			if ('error' in command && command.error) {
				setStep('finished');
			}
		} catch {
			setStep('finished');
		}
	}, [step, insuranceKey, dialogs]);

	useEffect(() => {
		if (!watchedCommandId) {
			return;
		}

		const command = commands[watchedCommandId];

		if (!command) {
			return;
		}

		if (
			command.state === 'done' &&
			command.operation === INSURANCE_OPERATION &&
			handledCommandsRef.current.indexOf(watchedCommandId) === -1
		) {
			handledCommandsRef.current = [
				...handledCommandsRef.current,
				watchedCommandId,
			];
			handleInsuranceResult(
				(command as { result?: InsuranceCommandResult })
					.result as InsuranceCommandResult,
			);
			return;
		}

		const isTerminal =
			command.state === 'error' ||
			command.state === 'offline' ||
			command.state === 'manual';

		if (!isTerminal) {
			return;
		}

		if (handledCommandsRef.current.indexOf(watchedCommandId) !== -1) {
			return;
		}

		handledCommandsRef.current = [
			...handledCommandsRef.current,
			watchedCommandId,
		];

		if (command.state === 'error') {
			failWithModal(step, (command as { errorMessage?: string }).errorMessage);
			return;
		}

		skipWithWarning('nczi.patientAutoVerify.skippedQueued');
	}, [commands, watchedCommandId]);

	const closeDialog = () => setDialogs(prevState => prevState.slice(1));

	useEffect(() => () => clearLoadingToast(), []);

	const retry = () => {
		const stepToRetry = failedStep;
		setFailedStep(null);
		setErrorMessage(undefined);
		closeDialog();

		if (stepToRetry === 'identifier' || stepToRetry === 'informations') {
			informationsVerifiedRef.current = false;
			mismatchFoundRef.current = false;
			startLoadingToast('nczi.patientAutoVerify.checkingInformations');
		}

		if (stepToRetry === 'insurance') {
			insuranceRequestedRef.current = false;
		}

		setStep(stepToRetry ?? 'finished');
	};

	return {
		activeDialog: dialogs[0],
		mismatchKeys,
		errorMessage,
		closeDialog,
		retry,
	};
}
